import React from 'react'

import AreYouSureModal from '../../components/molecules/ChatModals/AreYouSureModal'
import useUserPermissions from '../../hooks/permissions/useUserPermissions'

import useAccess, { PERMS } from '../../hooks/useAccess'

const AddRemoveParticipantContext = React.createContext(null)

export const INTERNAL = 'USERS'
export const EXTERNAL = 'EXTERNAL CONTACTS'

const AddRemoveParticipantProvider = ({ children }) => {
  const hasPermission = useAccess()
  const { hasBasic } = useUserPermissions()

  const [editSessionUsers, setEditSessionUsers] = React.useState(false)
  const [currentUserIDs, setCurrentUserIDs] = React.useState([])
  const [search, _setSearch] = React.useState(INTERNAL)
  const [addedUsers, setAddedUsers] = React.useState([])
  const [editIDs, setEditIDs] = React.useState([])
  const [leave, setLeave] = React.useState(false)
  const [switchClient, setSwitchClient] = React.useState(false)

  const options = React.useMemo(() => {
    const opts = []

    if (hasBasic) {
      opts.push(INTERNAL)
    }

    if (hasPermission([PERMS.ContactsOwn, PERMS.ContactsAudit])) {
      opts.push(EXTERNAL)
    }

    return opts
  }, [hasBasic, hasPermission])

  // Changes search when options change
  React.useEffect(() => {
    if (options.length === 1) {
      _setSearch(options[0])
    }
  }, [options])

  // clearing list on close
  React.useEffect(() => {
    if (!editSessionUsers) {
      setAddedUsers([])
    }
  }, [editSessionUsers])

  const handleClose = () => {
    setSwitchClient(false)
    _setSearch(INTERNAL)
  }

  const handleAcceptClientSearch = () => {
    setSwitchClient(false)
  }

  const setSearch = React.useCallback(val => {
    _setSearch(val)
    if (val === EXTERNAL) {
      setSwitchClient(true)
    }
  }, [])

  return (
    <>
      <AddRemoveParticipantContext.Provider
        value={{
          editSessionUsers,
          setEditSessionUsers,
          currentUserIDs,
          setCurrentUserIDs,
          options,
          setSearch,
          search,
          setAddedUsers,
          addedUsers,
          editIDs,
          setEditIDs,
          leave,
          setLeave
        }}
      >
        {children}
      </AddRemoveParticipantContext.Provider>

      {switchClient && (
        <AreYouSureModal
          open={switchClient}
          onClose={() => handleClose()}
          onAccept={handleAcceptClientSearch}
          reason='Are you sure you want to add an external contact to this chat?'
          subtext='Doing so will add them to this conversation'
        />
      )}
    </>
  )
}

export default AddRemoveParticipantProvider

export const useAddRemoveParticipant = () =>
  React.useContext(AddRemoveParticipantContext)
